import type { GetAgentsQuery } from '#src/graphql/generated';

import { useMutation, useQuery } from '@apollo/client/react';
import {
  Box,
  Button,
  DialogActionTrigger,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  HStack,
  Input,
  Switch,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';

import { JsonEditor } from '#src/components/json-editor';
import { CenteredDialogContent } from '#src/components/ui/centered-dialog-content';
import { toaster } from '#src/components/ui/toaster';
import { UPSERT_AGENT } from '#src/graphql/mutations/upsert-agent';
import { GET_AGENTS } from '#src/graphql/queries/agents';
import { GET_PROCESSORS } from '#src/graphql/queries/processors';

type AgentRow = GetAgentsQuery['agents']['agents'][number];

function parametersToJsonString(params: unknown): string {
  if (params == null || params === '') return '{}';
  if (typeof params === 'string') {
    try {
      return JSON.stringify(JSON.parse(params), null, 2);
    } catch {
      return '{}';
    }
  }
  try {
    return JSON.stringify(params, null, 2);
  } catch {
    return '{}';
  }
}

export function AgentModal({
  open,
  onOpenChange,
  mode,
  editing,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  editing: AgentRow | null;
  onSaved: () => void;
}) {
  const [name, setName] = useState('');
  const [cron, setCron] = useState('');
  const [processorId, setProcessorId] = useState('');
  const [parametersJson, setParametersJson] = useState('{}');
  const [enabled, setEnabled] = useState(true);

  const { data: processorsData } = useQuery(GET_PROCESSORS);
  const processorIds =
    processorsData?.processors?.processors?.map((p) => p.id) ?? [];

  const [upsertAgent, { loading: saving }] = useMutation(UPSERT_AGENT, {
    refetchQueries: [{ query: GET_AGENTS }],
  });

  useEffect(() => {
    if (!open) return;
    if (mode === 'create') {
      setName('');
      setCron('');
      setProcessorId('agent-edit-document');
      setParametersJson(
        JSON.stringify(
          {
            documentId: '',
            instructions: '',
          },
          null,
          2,
        ),
      );
      setEnabled(true);
      return;
    }
    if (!editing) return;
    setName(editing.name);
    setCron(editing.cron);
    setProcessorId(editing.processorId);
    setParametersJson(parametersToJsonString(editing.parameters));
    setEnabled(editing.enabled);
  }, [open, mode, editing]);

  const handleSave = useCallback(async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      toaster.create({
        title: 'Name required',
        type: 'warning',
      });
      return;
    }
    const trimmedCron = cron.trim();
    if (!trimmedCron) {
      toaster.create({
        title: 'Cron required',
        type: 'warning',
      });
      return;
    }
    const trimmedProcessor = processorId.trim();
    if (!trimmedProcessor) {
      toaster.create({
        title: 'Processor id required',
        type: 'warning',
      });
      return;
    }
    let parameters: unknown;
    try {
      parameters = JSON.parse(parametersJson || '{}') as unknown;
      if (parameters !== null && typeof parameters !== 'object') {
        throw new Error('parameters must be a JSON object');
      }
    } catch (e) {
      toaster.create({
        title: 'Invalid parameters JSON',
        description: e instanceof Error ? e.message : 'Parse error',
        type: 'error',
      });
      return;
    }

    try {
      await upsertAgent({
        variables: {
          input: {
            ...(mode === 'edit' && editing ? { id: editing.id } : {}),
            name: trimmedName,
            cron: trimmedCron,
            processorId: trimmedProcessor,
            parameters: parameters as object,
            enabled,
          },
        },
      });
      toaster.create({
        title: mode === 'create' ? 'Agent created' : 'Agent updated',
        type: 'success',
        duration: 2500,
        closable: true,
      });
      onSaved();
      onOpenChange(false);
    } catch (err: unknown) {
      const message =
        err &&
        typeof err === 'object' &&
        'message' in err &&
        typeof (err as { message: string }).message === 'string'
          ? (err as { message: string }).message
          : 'Save failed';
      toaster.create({
        title: 'Error',
        description: message,
        type: 'error',
      });
    }
  }, [
    name,
    cron,
    processorId,
    parametersJson,
    enabled,
    mode,
    editing,
    upsertAgent,
    onSaved,
    onOpenChange,
  ]);

  return (
    <DialogRoot
      open={open}
      onOpenChange={(e) => {
        if (!e.open) onOpenChange(false);
      }}
    >
      <CenteredDialogContent maxW="min(92vw, 40rem)">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Add agent' : 'Edit agent'}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <VStack gap={3} align="stretch">
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={1}>
                Name
              </Text>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="My scheduled agent"
                disabled={saving}
              />
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={1}>
                Cron
              </Text>
              <Input
                value={cron}
                onChange={(e) => setCron(e.target.value)}
                placeholder="0 9 * * *"
                disabled={saving}
                fontFamily="mono"
                fontSize="sm"
              />
              <Text fontSize="xs" color="fg.muted" mt={1}>
                Schedule expression for when this agent should run (execution is
                outside this app).
              </Text>
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={1}>
                Processor id
              </Text>
              <Input
                value={processorId}
                onChange={(e) => setProcessorId(e.target.value)}
                placeholder="agent-edit-document"
                disabled={saving}
                fontFamily="mono"
                fontSize="sm"
                list="agent-processor-ids"
              />
              <datalist id="agent-processor-ids">
                {processorIds.map((id) => (
                  <option key={id} value={id} />
                ))}
              </datalist>
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={2}>
                Parameters (JSON)
              </Text>
              <JsonEditor
                value={parametersJson}
                onChange={setParametersJson}
                placeholder='{"documentId":"…","instructions":"…"}'
                height="min(55vh, 420px)"
                disabled={saving}
              />
            </Box>
            <HStack align="center" gap={3}>
              <Text fontSize="sm" fontWeight="medium">
                Enabled
              </Text>
              <Switch.Root
                checked={enabled}
                onCheckedChange={(e) => setEnabled(Boolean(e.checked))}
                disabled={saving}
              >
                <Switch.HiddenInput />
                <Switch.Control>
                  <Switch.Thumb />
                </Switch.Control>
              </Switch.Root>
            </HStack>
          </VStack>
        </DialogBody>
        <DialogFooter>
          <HStack gap={2} flexWrap="wrap">
            <DialogActionTrigger asChild>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
            </DialogActionTrigger>
            <Button
              colorPalette="blue"
              loading={saving}
              disabled={
                saving || !name.trim() || !cron.trim() || !processorId.trim()
              }
              onClick={() => void handleSave()}
            >
              Save
            </Button>
          </HStack>
        </DialogFooter>
      </CenteredDialogContent>
    </DialogRoot>
  );
}
